import React, { ReactNode } from "react";
import { Col, Container, Row, Spinner, Alert, ButtonGroup, Button } from "react-bootstrap";
import styled from "styled-components";

import { getAnalyticsWidget, createExportSchedule } from "../../../utils/Requests";
import MetricsTable, { ColumnGroup } from "../../../components/analytics/MetricsTable";
import POPIndicator from "../../../components/analytics/POPIndicator";
import { ExportButton, ScheduleExportModal } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import { PERIOD_FILTER_CONFIG, AnalyticsFilters } from "../../../types/analytics";

/**
 * Business Impressions page uses the "period" filter style (7/14/30 days selector)
 * Filter config: PERIOD_FILTER_CONFIG
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FILTER_CONFIG = PERIOD_FILTER_CONFIG;

const PageContainer = styled(Container)`
    margin-top: 20px;
`;

const HeaderRow = styled(Row)`
    margin-bottom: 20px;
`;

const LastUpdatedText = styled.p`
    font-size: 0.9em;
    color: #6c757d;
    margin-bottom: 5px;
`;

const FilterRow = styled(Row)`
    margin-bottom: 20px;
    justify-content: center;
`;

type TimePeriod = 7 | 14 | 30;

type BusinessImpressionsProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type AggregatedCustomerData = {
    customer: string;
    visitors: number;
    monthlyActiveUsers: number;
    prevMonthActiveUsers: number;
    playsImp: number;
    avgWatchTimePerUserSecs: number;
    avgSessionsPerUser: number;
    adImp: number;
    // Previous period values for POP comparison
    prevVisitors: number;
    prevPlaysImp: number;
    prevAvgWatchTime: number;
    prevAvgSessions: number;
    prevAdImp: number;
};

type BusinessImpressionsState = {
    rawData: Record<string, any>[];
    aggregatedData: AggregatedCustomerData[];
    isLoading: boolean;
    error: string | null;
    lastDate: string | null;
    timePeriod: TimePeriod;
    showScheduleModal: boolean;
};

// API response field names (snake_case from backend)
const API_FIELDS = {
    datepartition: "datepartition",
    customer: "customer",
    visitors: "visitors",
    monthlyActiveUsers: "monthly_active_users",
    prevMonthActiveUsers: "prev_month_active_users",
    mauChangeVsPrevMonth: "mau_change_vs_prev_month",
    playsImp: "plays_imp",
    avgWatchTimePerUserSecs: "avg_watch_time_per_user_secs",
    avgSessionsPerUser: "avg_sessions_per_user",
    adImp: "ad_imp"
} as const;

class BusinessImpressions extends React.Component<BusinessImpressionsProps, BusinessImpressionsState> {
    private containerRef = React.createRef<HTMLDivElement>();

    constructor(props: BusinessImpressionsProps) {
        super(props);
        
        this.state = {
            rawData: [],
            aggregatedData: [],
            isLoading: true,
            error: null,
            lastDate: null,
            timePeriod: 7,
            showScheduleModal: false
        };
    }

    componentDidMount(): void {
        this.fetchData();
    }

    private fetchData = async (): Promise<void> => {
        try {
            this.setState({ isLoading: true, error: null });
            
            const response = await getAnalyticsWidget(
                this.props.customerConfig,
                "business_impressions"
            );
            
            // API returns { result: [...] }, extract the array
            const responseAny = response as any;
            const dataArray = responseAny?.result || (Array.isArray(response) ? response : []);
            
            if (dataArray && Array.isArray(dataArray) && dataArray.length > 0) {
                // Sort by date descending (most recent first)
                const sortedData = dataArray.sort((a: Record<string, any>, b: Record<string, any>) => 
                    (b[API_FIELDS.datepartition] || "").localeCompare(a[API_FIELDS.datepartition] || "")
                );
                
                const lastDate = sortedData.length > 0 ? sortedData[0][API_FIELDS.datepartition] : null;
                
                this.setState({
                    rawData: sortedData,
                    lastDate,
                    isLoading: false
                }, () => this.aggregateData());
            } else {
                this.setState({
                    rawData: [],
                    aggregatedData: [],
                    isLoading: false
                });
            }
        } catch (error) {
            this.setState({
                error: "Failed to load business impressions data",
                isLoading: false
            });
        }
    };

    private aggregateData = (): void => {
        const { rawData, timePeriod, lastDate } = this.state;
        
        if (!lastDate || rawData.length === 0) {
            this.setState({ aggregatedData: [] });
            return;
        }

        const lastDateObj = new Date(lastDate);
        const periodStartDate = new Date(lastDateObj);
        periodStartDate.setDate(periodStartDate.getDate() - timePeriod + 1);
        
        const prevPeriodEndDate = new Date(periodStartDate);
        prevPeriodEndDate.setDate(prevPeriodEndDate.getDate() - 1);
        const prevPeriodStartDate = new Date(prevPeriodEndDate);
        prevPeriodStartDate.setDate(prevPeriodStartDate.getDate() - timePeriod + 1);

        // Group data by customer
        const customerMap = new Map<string, {
            currentPeriod: Record<string, any>[];
            prevPeriod: Record<string, any>[];
        }>();

        rawData.forEach(row => {
            const customer = row[API_FIELDS.customer];
            const dateStr = row[API_FIELDS.datepartition];
            const rowDate = new Date(dateStr);

            if (!customerMap.has(customer)) {
                customerMap.set(customer, { currentPeriod: [], prevPeriod: [] });
            }

            const customerData = customerMap.get(customer)!;
            
            if (rowDate >= periodStartDate && rowDate <= lastDateObj) {
                customerData.currentPeriod.push(row);
            } else if (rowDate >= prevPeriodStartDate && rowDate <= prevPeriodEndDate) {
                customerData.prevPeriod.push(row);
            }
        });

        // Aggregate metrics for each customer
        const aggregatedData: AggregatedCustomerData[] = [];

        customerMap.forEach((data, customer) => {
            const current = data.currentPeriod;
            const prev = data.prevPeriod;

            // Sum/average metrics for current period
            const sumMetric = (arr: Record<string, any>[], field: string) => 
                arr.reduce((sum, r) => sum + (r[field] || 0), 0);
            
            const avgMetric = (arr: Record<string, any>[], field: string) => {
                if (arr.length === 0) return 0;
                return sumMetric(arr, field) / arr.length;
            };

            // Get latest MAU values (they're monthly, not daily sums)
            const latestRow = current.length > 0 ? current[0] : null;

            aggregatedData.push({
                customer,
                visitors: sumMetric(current, API_FIELDS.visitors),
                monthlyActiveUsers: latestRow?.[API_FIELDS.monthlyActiveUsers] || 0,
                prevMonthActiveUsers: latestRow?.[API_FIELDS.prevMonthActiveUsers] || 0,
                playsImp: sumMetric(current, API_FIELDS.playsImp),
                avgWatchTimePerUserSecs: Math.round(avgMetric(current, API_FIELDS.avgWatchTimePerUserSecs)),
                avgSessionsPerUser: avgMetric(current, API_FIELDS.avgSessionsPerUser),
                adImp: sumMetric(current, API_FIELDS.adImp),
                // Previous period for POP
                prevVisitors: sumMetric(prev, API_FIELDS.visitors),
                prevPlaysImp: sumMetric(prev, API_FIELDS.playsImp),
                prevAvgWatchTime: Math.round(avgMetric(prev, API_FIELDS.avgWatchTimePerUserSecs)),
                prevAvgSessions: avgMetric(prev, API_FIELDS.avgSessionsPerUser),
                prevAdImp: sumMetric(prev, API_FIELDS.adImp)
            });
        });

        // Sort by customer name
        aggregatedData.sort((a, b) => a.customer.localeCompare(b.customer));

        this.setState({ aggregatedData });
    };

    private handleTimePeriodChange = (period: TimePeriod): void => {
        this.setState({ timePeriod: period }, () => this.aggregateData());
    };

    private formatNumber = (value: number | null | undefined): string => {
        if (value == null) return "-";
        return value.toLocaleString();
    };

    private formatDecimal = (value: number | null | undefined): string => {
        if (value == null) return "-";
        return value.toFixed(1);
    };

    private formatTime = (seconds: number | null | undefined): string => {
        if (seconds == null) return "-";
        if (seconds < 60) return `${seconds}s`;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    };

    private convertToAnalyticsFilters = (): AnalyticsFilters => {
        const { timePeriod, lastDate } = this.state;
        const endDate = lastDate || new Date().toISOString().split("T")[0];
        const startDateObj = new Date(endDate);
        startDateObj.setDate(startDateObj.getDate() - timePeriod + 1);
        const startDate = startDateObj.toISOString().split("T")[0];
        return {
            startDate,
            endDate,
            attributionId: [],
            brandId: [],
            deviceType: []
        };
    };

    private getExportData = (): ExportableData[] => {
        const { aggregatedData } = this.state;

        if (aggregatedData.length === 0) {
            return [];
        }

        return [{
            title: "Business Impressions",
            columns: [
                { key: "customer", label: "Customer" },
                { key: "visitors", label: "Visitors", format: "number" },
                { key: "monthlyActiveUsers", label: "MAU", format: "number" },
                { key: "playsImp", label: "Plays Imp", format: "number" },
                { key: "avgWatchTimePerUserSecs", label: "Avg Watch Time (sec)", format: "number" },
                { key: "avgSessionsPerUser", label: "Avg Sessions/User", format: "number" },
                { key: "adImp", label: "Ad Imp", format: "number" }
            ],
            rows: aggregatedData,
            metadata: {
                filters: this.convertToAnalyticsFilters(),
                pageKey: "business_impressions",
                customerSlug: this.props.customerConfig.slug
            }
        }];
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Business Impressions",
            pageKey: "business_impressions",
            customerSlug: this.props.customerConfig.slug,
            filters: this.convertToAnalyticsFilters(),
            getData: this.getExportData,
            getContainerRef: () => this.containerRef.current
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private getColumnGroups = (): ColumnGroup[] => {
        return [
            {
                name: "Customer",
                columns: [
                    {
                        key: "customer",
                        header: "Name",
                        render: (value: string) => <strong>{value || "-"}</strong>
                    }
                ]
            },
            {
                name: "Visitors",
                columns: [
                    {
                        key: "visitors",
                        header: "Total",
                        render: (value: number, row: Record<string, any>) => (
                            <POPIndicator
                                currentValue={value}
                                previousValue={row.prevVisitors || null}
                                formatValue={this.formatNumber}
                            />
                        )
                    }
                ]
            },
            {
                name: "MAU's",
                columns: [
                    {
                        key: "monthlyActiveUsers",
                        header: "Count",
                        render: (value: number, row: Record<string, any>) => (
                            <POPIndicator
                                currentValue={value}
                                previousValue={row.prevMonthActiveUsers || null}
                                formatValue={this.formatNumber}
                            />
                        )
                    }
                ]
            },
            {
                name: "Plays Imp",
                columns: [
                    {
                        key: "playsImp",
                        header: "Total",
                        render: (value: number, row: Record<string, any>) => (
                            <POPIndicator
                                currentValue={value}
                                previousValue={row.prevPlaysImp || null}
                                formatValue={this.formatNumber}
                            />
                        )
                    }
                ]
            },
            {
                name: "Avg Watch Time",
                columns: [
                    {
                        key: "avgWatchTimePerUserSecs",
                        header: "Per User",
                        render: (value: number, row: Record<string, any>) => (
                            <POPIndicator
                                currentValue={value}
                                previousValue={row.prevAvgWatchTime || null}
                                formatValue={this.formatTime}
                            />
                        )
                    }
                ]
            },
            {
                name: "Avg Sessions",
                columns: [
                    {
                        key: "avgSessionsPerUser",
                        header: "Per User",
                        render: (value: number, row: Record<string, any>) => (
                            <POPIndicator
                                currentValue={value}
                                previousValue={row.prevAvgSessions || null}
                                formatValue={this.formatDecimal}
                            />
                        )
                    }
                ]
            },
            {
                name: "Ad Imp",
                columns: [
                    {
                        key: "adImp",
                        header: "Total",
                        render: (value: number, row: Record<string, any>) => (
                            <POPIndicator
                                currentValue={value}
                                previousValue={row.prevAdImp || null}
                                formatValue={this.formatNumber}
                            />
                        )
                    }
                ]
            }
        ];
    };

    public render(): ReactNode {
        const { aggregatedData, isLoading, error, lastDate, timePeriod } = this.state;
        const columnGroups = this.getColumnGroups();

        return (
            <div ref={this.containerRef}>
            <PageContainer className="mw-100" style={{ position: "relative" }}>
                <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000 }}>
                    <ExportButton
                        context={this.getExportContext()}
                        enabled={!isLoading && !error && aggregatedData.length > 0}
                        scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                        onScheduleClick={() => this.setState({ showScheduleModal: true })}
                    />
                </div>

                <HeaderRow>
                    <Col md style={{ textAlign: "center" }}>
                        <h2>Business Impressions</h2>
                        {lastDate && (
                            <LastUpdatedText>
                                Latest data: {new Date(lastDate).toLocaleDateString()}
                            </LastUpdatedText>
                        )}
                    </Col>
                </HeaderRow>

                <FilterRow>
                    <Col md="auto">
                        <ButtonGroup>
                            <Button
                                variant={timePeriod === 7 ? "primary" : "outline-primary"}
                                onClick={() => this.handleTimePeriodChange(7)}
                            >
                                7 Days
                            </Button>
                            <Button
                                variant={timePeriod === 14 ? "primary" : "outline-primary"}
                                onClick={() => this.handleTimePeriodChange(14)}
                            >
                                14 Days
                            </Button>
                            <Button
                                variant={timePeriod === 30 ? "primary" : "outline-primary"}
                                onClick={() => this.handleTimePeriodChange(30)}
                            >
                                30 Days
                            </Button>
                        </ButtonGroup>
                    </Col>
                </FilterRow>

                {isLoading ? (
                    <Row className="justify-content-center">
                        <Col className="text-center" style={{ padding: "40px" }}>
                            <Spinner animation="border" variant="primary" />
                            <p style={{ marginTop: "10px" }}>Loading business impressions data...</p>
                        </Col>
                    </Row>
                ) : error ? (
                    <Row>
                        <Col>
                            <Alert variant="danger">{error}</Alert>
                        </Col>
                    </Row>
                ) : (
                    <Row>
                        <Col>
                            <MetricsTable
                                data={aggregatedData}
                                columnGroups={columnGroups}
                                rowKeyField="customer"
                                emptyMessage="No business impressions data available"
                            />
                        </Col>
                    </Row>
                )}

                <ScheduleExportModal
                    show={this.state.showScheduleModal}
                    onHide={() => this.setState({ showScheduleModal: false })}
                    filters={this.convertToAnalyticsFilters()}
                    pageKey="business_impressions"
                    pageTitle="Business Impressions"
                    customerSlug={this.props.customerConfig.slug}
                    editingSchedule={null}
                    onSave={this.handleScheduleSave}
                />
            </PageContainer>
            </div>
        );
    }
}

export default BusinessImpressions;
